import './NotificationsPage.scss';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../i18n';
import { get, post } from '../../services/api';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Tabs from '../../components/Tabs';
import EmptyState from '../../components/EmptyState';
import Skeleton from '../../components/Skeleton';
import type { Notification, Paginated } from '../../types';

type TabKey = 'all' | 'unread';

export default function NotificationsPage() {
  const { t } = useLang();
  const [tab, setTab] = useState<TabKey>('all');
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const tabs = useMemo(() => ([
    { value: 'all', label: t('notifications.tab.all') },
    { value: 'unread', label: t('notifications.tab.unread') },
  ]), [t]);

  const reload = async () => {
    try {
      const res = await get<Paginated<Notification> | Notification[]>('/me/notifications/');
      const list = Array.isArray(res) ? res : (res.results || []);
      const filtered = tab === 'unread' ? list.filter((n: any) => !n.is_read) : list;
      setItems(filtered);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    reload();
    const id = window.setInterval(reload, 30000);
    return () => window.clearInterval(id);
  }, [tab]);

  const markRead = async (id: number) => {
    try {
      await post(`/me/notifications/${id}/mark-read/`);
      setItems((arr) => arr.map((n) => n.id === id ? { ...n, is_read: true } : n));
    } catch { void 0; }
  };

  const markAll = async () => {
    try {
      await post('/me/notifications/mark-all-read/');
      setItems((arr) => arr.map((n) => ({ ...n, is_read: true })));
    } catch { void 0; }
  };

  const targetUrl = (n: any): string | null => {
    if (n?.payload?.order_public_id) {
      return `/cabinet/orders/${n.payload.order_public_id}`;
    }
    if (n?.link) return n.link;
    return null;
  };

  return (
    <div className="notif-page">
      <header className="notif-page__head">
        <h1 className="notif-page__title">{t('notifications.title')}</h1>
        <Button variant="secondary" size="sm" onClick={markAll}>
          {t('notifications.mark_all_read')}
        </Button>
      </header>
      <Tabs value={tab} onChange={(v) => setTab(v as TabKey)} items={tabs} ariaLabel={t('notifications.title')} />
      {loading ? (
        <Skeleton height={200} />
      ) : items.length === 0 ? (
        <Card padding="lg"><EmptyState title={t('notifications.empty')} /></Card>
      ) : (
        <ul className="notif-page__list">
          {items.map((n) => (
            <li key={n.id}>
              <Card padding="md" className={['notif-page__item', n.is_read ? '' : 'notif-page__item--unread'].filter(Boolean).join(' ')}>
                <div className="notif-page__row">
                  <div className="notif-page__info">
                    <div className="notif-page__title-text">{n.title}</div>
                    <p className="notif-page__body">{n.body}</p>
                    {targetUrl(n) ? <Link to={targetUrl(n)!} className="notif-page__link" onClick={() => !n.is_read && markRead(n.id)}>{t('notifications.open')}</Link> : null}
                    <div className="notif-page__time">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.is_read ? (
                    <Button variant="ghost" size="sm" onClick={() => markRead(n.id)}>{t('notifications.mark_read')}</Button>
                  ) : null}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
